'use client'

import React from 'react'

import { DEFAULT_CURRENCY_FORMAT } from './currency.constants'
import { useFormattedPrice } from './currency.hook'

import type { CurrencyCode, CurrencyFormatName, PriceInput } from './currency.types'

export interface FormattedPriceProps {
  /**
   * The numeric amount to format (returns null if invalid)
   */
  amount: PriceInput

  /**
   * The semantic format name for display
   * @default 'NARROW_SYMBOL'
   */
  formatName?: CurrencyFormatName

  /**
   * Override the currency (defaults to region's currency)
   */
  currency?: CurrencyCode

  /**
   * Optional CSS class name
   */
  className?: string

  /**
   * Optional inline styles
   */
  style?: React.CSSProperties

  /**
   * Optional data-testid for testing
   */
  'data-testid'?: string

  /**
   * Whether to wrap in a semantic <data> element (default: true)
   * Set to false to render just the formatted string in a fragment
   */
  asData?: boolean
}

/**
 * FormattedPrice component renders a locale-aware formatted price.
 *
 * Uses the current locale and region to determine currency symbol placement,
 * decimal/thousand separators, and currency code.
 *
 * Renders a semantic `<data>` element with machine-readable value attribute.
 *
 * @example Standard price
 * ```tsx
 * <FormattedPrice amount={99.99} />
 * // Renders: <data value="99.99" data-currency="USD">$99.99</data>
 * ```
 *
 * @example Canadian dollar in French Canadian locale
 * ```tsx
 * <FormattedPrice amount={99.99} currency="CAD" />
 * // In fr-CA: <data value="99.99" data-currency="CAD">99,99 $</data>
 * ```
 *
 * @example Without wrapper element
 * ```tsx
 * <FormattedPrice amount={99.99} asData={false} />
 * // Renders: $99.99 (plain text)
 * ```
 */
export function FormattedPrice({
  amount,
  formatName = DEFAULT_CURRENCY_FORMAT,
  currency,
  className,
  style,
  'data-testid': dataTestId,
  asData = true,
}: FormattedPriceProps) {
  const {
    formatted,
    value,
    currency: resolvedCurrency,
  } = useFormattedPrice(amount, formatName, {
    currency,
  })

  // Return null for invalid amounts (null, undefined, NaN)
  if (value === null) {
    return null
  }

  if (!asData) {
    return <>{formatted}</>
  }

  return (
    <data value={value} data-currency={resolvedCurrency} className={className} style={style} data-testid={dataTestId}>
      {formatted}
    </data>
  )
}

FormattedPrice.displayName = 'FormattedPrice'

export default FormattedPrice
